import { collection, query, getDocs, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeText, tokenizeName, levenshteinDistance } from '../utils/textUtils';
import { calculateUnifiedScore, checkDistributedMatches } from '../utils/scoringUtils';
import { generateVerificationId } from '../utils/verificationUtils';

// Constants
const MIN_SCORE_THRESHOLD = 45; // Minimum score to include in results (Low confidence and above)

/**
 * Calculate token similarity for fuzzy matching
 * @param {string} token1 - First token to compare
 * @param {string} token2 - Second token to compare
 * @returns {number} Similarity score (0-1)
 */
const calculateTokenSimilarity = (token1, token2) => {
  if (!token1 || !token2) return 0;
  
  // Exact match
  if (token1 === token2) return 1.0;
  
  // Calculate Levenshtein distance
  const distance = levenshteinDistance(token1, token2);
  const maxLength = Math.max(token1.length, token2.length);
  
  // Convert distance to similarity (0-1)
  let similarityScore;
  
  if (maxLength <= 3) {
    // For very short strings, only accept exact matches
    similarityScore = distance === 0 ? 1.0 : 0;
  } else if (maxLength <= 6) {
    // For short strings, be more strict
    similarityScore = distance === 0 ? 1.0 : 
                     distance === 1 ? 0.7 : 
                     distance === 2 ? 0.3 : 0;
  } else {
    // For longer strings, use scaled similarity
    similarityScore = Math.max(0, 1 - (distance / (maxLength * 0.7)));
  }
  
  // Add bonus for containment
  if (token1.length > 3 && token2.length > 3 &&
      (token1.includes(token2) || token2.includes(token1))) {
    const containmentBonus = 0.15;
    similarityScore = Math.min(1.0, similarityScore + containmentBonus);
  }
  
  return similarityScore;
};

/**
 * Check if there's a fuzzy match between document tokens and search tokens
 * @param {string[]} docTokens - Document tokens to check
 * @param {string[]} searchTokens - Search tokens to match against
 * @param {number} threshold - Similarity threshold (0-1)
 * @returns {boolean} Whether there's a match
 */
const checkTokensForFuzzyMatches = (docTokens, searchTokens, threshold = 0.7) => {
  if (!docTokens?.length || !searchTokens?.length) return false;
  
  // Check each search token against all document tokens
  for (const searchToken of searchTokens) {
    let bestScore = 0;
    
    for (const docToken of docTokens) {
      const similarity = calculateTokenSimilarity(searchToken, docToken);
      bestScore = Math.max(bestScore, similarity);
    }
    
    // If we found a good match for this token, count it as a match
    if (bestScore >= threshold) {
      return true;
    }
  }
  
  return false;
};

/**
 * Optimized search function that uses name tokens for efficient two-stage search
 * @param {string} searchTerm - The search term to look for
 * @param {Function} updateProgress - Callback to update search progress
 * @param {string} userId - The ID of the user performing the search (optional)
 * @param {string} entityType - Type of entity to search for ('person' or 'enterprise', null for both)
 * @returns {Object} Search results and statistics
 */
export const optimizedSearchSanctions = async (searchTerm, updateProgress, userId = null, entityType = null) => {
  try {
    console.time('optimizedSearch');
    
    // Generate a timestamp and verification ID for the search
    const searchTimestamp = Date.now();
    const verificationId = await generateVerificationId(searchTerm, searchTimestamp);
    
    // Normalize and tokenize the search term
    const searchTermNormalized = normalizeText(searchTerm);
    const searchTermTokens = tokenizeName(searchTerm);
    
    // Stats tracking
    const stats = {
      totalDocumentsQueried: 0,
      totalNamesProcessed: 0,
      firstStageFiltered: 0,
      secondStageMatches: 0,
      totalProcessingTime: 0,
      entityTypeFilter: entityType || 'all'
    };
    
    console.log(`Searching for: "${searchTerm}" (tokens: ${searchTermTokens.join(', ')}), entity type: ${entityType || 'all'}`);
    
    // FIRST STAGE: Get all active sanctions with minimal fields
    const sanctionsRef = collection(db, 'sanctions');
    
    // Create query based on entity type filter
    let baseQuery;
    if (entityType) {
      baseQuery = query(sanctionsRef, 
        where("status", "==", "active"),
        where("entityType", "==", entityType)
      );
    } else {
      baseQuery = query(sanctionsRef, where("status", "==", "active"));
    }
    
    const querySnapshot = await getDocs(baseQuery);
    
    stats.totalDocumentsQueried = querySnapshot.size;
    
    // Update progress with the total documents
    if (updateProgress) {
      updateProgress({
        processed: 0,
        total: stats.totalDocumentsQueried,
        stage: 'first'
      });
    }
    
    console.log(`First stage: Processing ${stats.totalDocumentsQueried} active sanctions documents ${entityType ? `(entityType: ${entityType})` : ''}`);
    
    // First-stage filtering: Find documents with potential token matches
    const potentialMatches = [];
    let processedCount = 0;
    
    // Add time-based progress updates for the first stage
    const startTime = Date.now();
    let lastUpdateTime = startTime;
    
    // Setup an artificial progress indicator for better UX during first stage
    if (updateProgress) {
      updateProgress({
        processed: 1,
        total: stats.totalDocumentsQueried,
        stage: 'first'
      });
    }
    
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      processedCount++;
      
      // Update progress periodically based on count
      if (updateProgress && processedCount % 50 === 0) {
        updateProgress({
          processed: processedCount,
          total: stats.totalDocumentsQueried,
          stage: 'first'
        });
      }
      
      // Also update progress based on time intervals (every 300ms)
      // This ensures the progress bar moves even if processing is slow
      const currentTime = Date.now();
      if (updateProgress && (currentTime - lastUpdateTime > 300)) {
        // Calculate an artificial progress that gradually increases
        // This creates a smoother user experience
        const elapsedMs = currentTime - startTime;
        // Use a non-linear progression that starts faster and slows down
        // This gives users a sense of progress while being more realistic
        const artificialProgress = Math.min(
          processedCount,
          Math.ceil((elapsedMs / 1000) * (stats.totalDocumentsQueried / 8))
        );
        
        updateProgress({
          processed: artificialProgress,
          total: stats.totalDocumentsQueried,
          stage: 'first'
        });
        
        lastUpdateTime = currentTime;
      }
      
      // Skip documents with no names or nameTokensSet
      if (!data.nameTokensSet || !data.names || !data.names.length) {
        continue;
      }
      
      // Use fuzzy matching on nameTokensSet for first-stage filtering
      const hasFuzzyMatch = checkTokensForFuzzyMatches(
        data.nameTokensSet,
        searchTermTokens,
        0.7 // Threshold for considering a match
      );
      
      if (hasFuzzyMatch) {
        potentialMatches.push({
          id: doc.id,
          data
        });
      }
    }
    
    stats.firstStageFiltered = potentialMatches.length;
    
    console.log(`First stage complete: Found ${stats.firstStageFiltered} potential matches out of ${stats.totalDocumentsQueried} documents`);
    
    // Add a brief 'calculating' phase to provide visual feedback for the transition
    if (updateProgress) {
      // Show 100% completion of first stage
      updateProgress({
        processed: stats.totalDocumentsQueried,
        total: stats.totalDocumentsQueried,
        stage: 'first'
      });
      
      // Add a small delay to show the completion of the first stage
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Then update to preparation phase for second stage
      updateProgress({
        processed: 0,
        total: potentialMatches.length || 1, // Avoid division by zero
        stage: 'preparing'
      });
      
      // Another small delay to show the transition
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    // Update progress for second stage
    if (updateProgress) {
      updateProgress({
        processed: 0,
        total: potentialMatches.length || 1, // Avoid division by zero
        stage: 'second'
      });
    }
    
    // SECOND STAGE: Detailed matching on potential matches
    const matchingResults = [];
    processedCount = 0;
    
    // Process potential matches with detailed scoring
    for (const { id, data } of potentialMatches) {
      processedCount++;
      
      // Update progress periodically
      if (updateProgress && processedCount % 5 === 0) {
        updateProgress({
          processed: processedCount,
          total: potentialMatches.length,
          stage: 'second'
        });
      }
      
      // Check for distributed matches
      const distributedMatch = checkDistributedMatches(data, searchTermTokens);
      
      // Create a base record with common information
      const baseRecord = {
        id,
        docData: data,
        remark: data.remark || '',
        designationDetails: data.designationDetails || '',
        subjectType: data.subjectType?.code || '',
        citizenships: (data.citizenships || []).map(c => c.countryDescription).join(', '),
        birthdates: (data.birthdates || []).map(b => b.birthdate || 
          `${b.year || ''}${b.year && (b.dayOfMonth || b.monthOfYear) ? '-' : ''}${b.monthOfYear || ''}${b.monthOfYear && b.dayOfMonth ? '-' : ''}${b.dayOfMonth || ''}`
        ).filter(Boolean).join(', '),
        isDistributedMatch: distributedMatch.isDistributedMatch,
        distributionInfo: distributedMatch.distribution,
        matchedTokens: distributedMatch.matchedTokens
      };
      
      // Track the best match for this document
      let bestMatch = null;
      let bestScore = 0;
      
      // Process each name within the document
      data.names.forEach((name) => {
        stats.totalNamesProcessed++;
        
        // Get full name
        const constructedName = `${name.title || ''} ${name.firstName || ''} ${name.middleName || ''} ${name.lastName || ''}`.trim();
        const fullName = name.wholeName ? name.wholeName : constructedName;
        
        if (!fullName) return;
        
        // Use the pre-computed normalized name if available
        const normalizedName = name.normalizedName || normalizeText(fullName);
        
        const nameEntry = {
          ...baseRecord,
          nameIndex: data.names.indexOf(name),
          fullName: fullName,
          normalizedName: normalizedName,
          firstName: name.firstName || '',
          middleName: name.middleName || '',
          lastName: name.lastName || '',
          gender: name.gender || '',
          function: name.function || '',
          title: name.title || '',
          strong: name.strong || false,
          qualityLevel: name.qualityLevel || 'UNKNOWN',
          qualityScore: name.qualityScore || 0,
          qualityFlags: name.qualityFlags || []
        };
        
        // Calculate unified score using the existing algorithm
        const score = calculateUnifiedScore(nameEntry, searchTermTokens, searchTermNormalized);
        nameEntry.unifiedScore = score;
        
        // Set confidence level
        if (score >= 95) {
          nameEntry.confidenceLevel = "Very High";
        } else if (score >= 80) {
          nameEntry.confidenceLevel = "High";
        } else if (score >= 65) {
          nameEntry.confidenceLevel = "Medium";
        } else if (score >= 45) {
          nameEntry.confidenceLevel = "Low";
        } else {
          nameEntry.confidenceLevel = "Very Low";
        }
        
        // Track best matching name variant
        if (score > bestScore) {
          bestScore = score;
          bestMatch = nameEntry;
        }
      });
      
      // If we have a match above threshold, add to results
      if (bestMatch && bestMatch.unifiedScore >= MIN_SCORE_THRESHOLD) {
        matchingResults.push(bestMatch);
      }
    }
    
    // Sort results by score (highest first)
    matchingResults.sort((a, b) => b.unifiedScore - a.unifiedScore);
    
    stats.secondStageMatches = matchingResults.length;
    
    // Limit results to reasonable number
    let limitedResults = matchingResults;
    if (matchingResults.length > 200) {
      console.log(`Limited ${matchingResults.length} results to top 200 by score`);
      limitedResults = matchingResults.slice(0, 200); // Limit to top 200 scores
    }
    
    // Group results by confidence level for stats
    const veryHighConfidence = matchingResults.filter(r => r.unifiedScore >= 95);
    const highConfidence = matchingResults.filter(r => r.unifiedScore >= 80 && r.unifiedScore < 95);
    const mediumConfidence = matchingResults.filter(r => r.unifiedScore >= 65 && r.unifiedScore < 80);
    const lowConfidence = matchingResults.filter(r => r.unifiedScore >= 45 && r.unifiedScore < 65);
    
    // Final stats
    const searchStats = {
      ...stats,
      totalMatches: matchingResults.length,
      veryHighConfidence: veryHighConfidence.length,
      highConfidence: highConfidence.length,
      mediumConfidence: mediumConfidence.length,
      lowConfidence: lowConfidence.length,
      verificationId: verificationId,
      searchTime: new Date().toISOString()
    };
    
    console.timeEnd('optimizedSearch');
    
    // Calculate total processing time
    searchStats.totalProcessingTime = Date.now() - searchTimestamp;
    
    console.log(`Optimized search complete: ${searchStats.totalMatches} matches found in ${searchStats.totalProcessingTime}ms`);
    console.log(`First stage filtered ${searchStats.firstStageFiltered} out of ${searchStats.totalDocumentsQueried} documents`);
    
    return {
      results: limitedResults,
      stats: searchStats
    };
    
  } catch (error) {
    console.error('Error in optimized search:', error);
    throw error;
  }
};

/**
 * Export the original search function to maintain compatibility
 */
export { searchSanctions } from './searchService'; 